import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { validateSoundParams } from "../../utils/validation/soundValidation";
import WaveSurfer from "wavesurfer.js";
import { useJunoModels } from "../../hooks/useJunoModels";
import Cookies from "js-cookie";
import { useTranslation } from "react-i18next";

export type SoundCategory = "music" | "sfx" | "lyric";

export type SoundEngineList = "CASSETTEAI" | "CASSETTEAI_SFX" | "DIFFRHYTHM" | "ELEVENLABS" | "MINIMAX" | "MMAUDIO";

export type SoundDuration = Record<SoundEngineList, number>;
export type SoundGuidanceScale = Record<SoundEngineList, number>;
export type SoundStep = Record<SoundEngineList, number>;

// Sound項目の型定義
export interface SoundItem {
  id: string;
  uuid: string;
  url: string | null;
  prompt: string;
  model: string;
  status: number;
  params: Record<string, unknown>;
  created_at: string;
}

interface SoundContextType {
  fetchSoundTrigger: boolean;
  setFetchSoundTrigger: (fetchSoundTrigger: boolean) => void;
  prompt: string;
  setPrompt: (prompt: string) => void;
  lyric: string;
  setLyric: (lyric: string) => void;
  negativePrompt: string;
  setNegativePrompt: (negativePrompt: string) => void;
  processing: boolean;
  setProcessing: (processing: boolean) => void;
  aiEngine: SoundEngineList;
  setSoundEngine: (aiEngine: SoundEngineList) => void;
  category: SoundCategory[];
  setCategory: (category: SoundCategory[]) => void;
  duration: SoundDuration;
  setDuration: (duration: number) => void;
  guidanceScale: SoundGuidanceScale;
  setGuidanceScale: (scale: number) => void;
  step: SoundStep;
  setStep: (step: number) => void;
  audio1: string;
  setAudio1: (audio: string) => void;
  public: boolean;
  setPublic: (isPublic: boolean) => void;
  credit: number | string | undefined;
  isCalculatingCredit: boolean;
  submitSound: (
    setGenerationCompleted: (completed: boolean) => void,
    setError: (error: string | null) => void,
    setAlert?: (type: string, message: string, path?: string) => void
  ) => Promise<void>;
  updateParams: (aiEngine: SoundEngineList, state: "duration" | "guidanceScale" | "step", value: number) => void;

  // 追加: 波形と再生機能のためのプロパティ
  wavesurferInstance: WaveSurfer | null;
  waveformRef: React.RefObject<HTMLDivElement>;
  isPlaying: boolean;
  isMuted: boolean;
  playingUrl: string | null;
  currentSound: SoundItem | null;
  sounds: SoundItem[];
  setSounds: (sounds: SoundItem[]) => void;
  toggleAudio: (url: string) => Promise<void>;
  toggleMute: () => void;
  previousSound: () => void;
  nextSound: () => void;
  downloadSoundFile: (uuid: string, filename: string) => Promise<void>;
  seekTo: (position: number) => void;
}

const SoundContext = createContext<SoundContextType>({
  fetchSoundTrigger: false,
  setFetchSoundTrigger: () => {},
  prompt: "",
  setPrompt: () => {},
  lyric: "",
  setLyric: () => {},
  negativePrompt: "",
  setNegativePrompt: () => {},
  processing: false,
  setProcessing: () => {},
  aiEngine: "" as SoundEngineList,
  setSoundEngine: () => {},
  category: [],
  setCategory: () => {},
  duration: {} as SoundDuration,
  setDuration: () => {},
  guidanceScale: {} as SoundGuidanceScale,
  setGuidanceScale: () => {},
  step: {} as SoundStep,
  setStep: () => {},
  audio1: "",
  setAudio1: () => {},
  public: false,
  setPublic: () => {},
  credit: undefined,
  isCalculatingCredit: false,
  submitSound: async () => {},
  updateParams: () => {},

  // 追加: 波形と再生機能のためのプロパティの初期値
  wavesurferInstance: null,
  waveformRef: { current: null },
  isPlaying: false,
  isMuted: false,
  playingUrl: null,
  currentSound: null,
  sounds: [],
  setSounds: () => {},
  toggleAudio: async () => {},
  toggleMute: () => {},
  previousSound: () => {},
  nextSound: () => {},
  downloadSoundFile: async () => {},
  seekTo: () => {},
});

export const useSound = () => useContext(SoundContext);

export const SoundProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  // WaveSurferインスタンスを保持するための参照
  const [wavesurferInstance, setWavesurferInstance] = useState<WaveSurfer | null>(null);
  const waveformRef = useRef<HTMLDivElement>(null);
  const isProcessing = useRef(false); // 処理中フラグ（再生・停止の重複防止）

  // 音声再生状態
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [playingUrl, setPlayingUrl] = useState<string | null>(null);
  const [currentSound, setCurrentSound] = useState<SoundItem | null>(null);
  const [sounds, setSounds] = useState<SoundItem[]>([]);
  const [soundBlobs, setSoundBlobs] = useState<Record<string, Blob>>({});
  const [fetchSoundTrigger, setFetchSoundTrigger] = useState<boolean>(false);
  const [prompt, setPrompt] = useState<string>("");
  const [lyric, setLyric] = useState<string>("");
  const [negativePrompt, setNegativePrompt] = useState<string>("");
  const [processing, setProcessing] = useState<boolean>(false);
  const [aiEngine, setSoundEngine] = useState<SoundEngineList>("MINIMAX");
  const [category, setCategory] = useState<SoundCategory[]>(["music"]);
  const [audio1, setAudio1] = useState<string>("");
  const [isPublic, setPublic] = useState<boolean>(true);
  const [credit, setCredit] = useState<number | string | undefined>(undefined);
  const [isCalculatingCredit, setIsCalculatingCredit] = useState(false);

  // モデルごとの設定値
  const [duration, setDurationState] = useState<SoundDuration>({
    CASSETTEAI: 30,
    CASSETTEAI_SFX: 30,
    DIFFRHYTHM: 95,
    ELEVENLABS: 15,
    MINIMAX: 30,
    MMAUDIO: 15,
  });

  const [guidanceScale, setGuidanceScaleState] = useState<SoundGuidanceScale>({
    CASSETTEAI: 7.0,
    CASSETTEAI_SFX: 7.0,
    DIFFRHYTHM: 4.0,
    ELEVENLABS: 0.3,
    MINIMAX: 7.0,
    MMAUDIO: 4.5,
  });

  const [step, setStepState] = useState<SoundStep>({
    CASSETTEAI: 50,
    CASSETTEAI_SFX: 50,
    DIFFRHYTHM: 32,
    ELEVENLABS: 50,
    MINIMAX: 50,
    MMAUDIO: 25,
  });

  // 設定用関数
  const setDuration = (value: number) => {
    setDurationState((prev) => ({ ...prev, [aiEngine]: value }));
  };

  const setGuidanceScale = (value: number) => {
    setGuidanceScaleState((prev) => ({ ...prev, [aiEngine]: value }));
  };

  const setStep = (value: number) => {
    setStepState((prev) => ({ ...prev, [aiEngine]: value }));
  };

  // パラメータ更新用の関数
  const updateParams = (engine: SoundEngineList, state: "duration" | "guidanceScale" | "step", value: number) => {
    switch (state) {
      case "duration":
        setDurationState((prev) => ({ ...prev, [engine]: value }));
        break;
      case "guidanceScale":
        setGuidanceScaleState((prev) => ({ ...prev, [engine]: value }));
        break;
      case "step":
        setStepState((prev) => ({ ...prev, [engine]: value }));
        break;
      default:
        break;
    }
  };

  // クレジット計算
  const fetchCredit = useCallback(async () => {
    const url = "/api/v1/juno/sound-credit";
    const csrftoken = Cookies.get("csrftoken");
    const headers = new Headers({
      "Content-Type": "application/json",
      "X-CSRFToken": csrftoken!,
    });

    const body = {
      model: aiEngine,
      duration: duration[aiEngine],
    };

    const response = await fetch(url, {
      method: "POST",
      headers: headers,
      body: JSON.stringify(body),
    });

    return response.json();
  }, [aiEngine, duration]);

  // aiEngineとdurationが変更されたらクレジットを計算する
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsCalculatingCredit(true);
      fetchCredit().then((data) => {
        if (data.status === "success") {
          setCredit(data.credit);
        }
        setIsCalculatingCredit(false);
      });
    }, 300);

    return () => clearTimeout(timer);
  }, [aiEngine, duration, fetchCredit]);

  const { getSoundModelParams } = useJunoModels();

  const submitSound = async (
    setGenerationCompleted: (completed: boolean) => void,
    setError: (error: string | null) => void,
    setAlert?: (type: string, message: string, path?: string) => void
  ) => {
    // 統合されたバリデーション
    const validationResult = validateSoundParams(aiEngine, {
      prompt,
      audio1,
      lyric,
      negativePrompt,
    });

    if (!validationResult.isValid) {
      setError(validationResult.errorKey);
      return;
    }

    try {
      setProcessing(true);
      setError(null);

      // モデルに必要なパラメータを取得
      const paramsArray = getSoundModelParams(aiEngine);

      // JSONデータを作成（FormDataの代わりに使用）
      const jsonData: Record<string, string | number | boolean | string[] | null> = {
        prompt: prompt,
        model: aiEngine,
        type: category.includes("music") ? "music" : "sfx",
      };

      // 必要なパラメータのみをJSONに追加
      paramsArray.forEach((param) => {
        switch (param) {
          case "duration":
            jsonData.duration = duration[aiEngine];
            break;
          case "guidanceScale":
            jsonData.guidance_scale = guidanceScale[aiEngine];
            break;
          case "step":
            jsonData.step = step[aiEngine];
            break;
          case "public":
            jsonData.is_public = isPublic;
            break;
          case "audioUpload":
            // Base64文字列としてそのまま送信（audioではなくaudio1として送信）
            if (audio1) {
              jsonData.audio1 = audio1;
            }
            break;
          case "lyric":
            if (lyric) {
              jsonData.lyric = lyric;
            }
            break;
          case "negativePrompt":
            if (negativePrompt) {
              jsonData.negative_prompt = negativePrompt;
            }
            break;
          default:
            break;
        }
      });

      // CSRFトークンを取得
      const csrftoken = Cookies.get("csrftoken");

      // 直接fetchを使用してJSONデータを送信
      const response = await fetch("/api/v1/juno/sound/generate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrftoken || "",
        },
        body: JSON.stringify(jsonData),
      });

      // レスポンスのステータスだけでチェックするため、データは取得しない

      if (response.status === 201) {
        setGenerationCompleted(true);
        setFetchSoundTrigger(true);
        if (setAlert) {
          setAlert("success", t("juno.sound.startGenerating"));
        }
      } else {
        setError("juno.sound.error.generate");
      }
    } catch (err) {
      console.error("Error generating sound:", err);
      setError("juno.sound.error.generate");
    } finally {
      setProcessing(false);
    }
  };

  // Wavesurferの作成（シンプル化）
  const createWavesurfer = useCallback(
    (container: HTMLElement, url: string) => {
      // 既存のインスタンスがあれば破棄
      if (wavesurferInstance) {
        try {
          wavesurferInstance.destroy();
        } catch (e) {
          console.error("Error destroying wavesurfer:", e);
        }
      }

      // 新しいインスタンスを作成
      const wavesurfer = WaveSurfer.create({
        container,
        waveColor: "#4f4f4f",
        progressColor: "#1976d2",
        cursorColor: "#1976d2",
        height: 40,
        barWidth: 2,
        barGap: 1,
        barRadius: 3,
        normalize: true, // 波形を正規化
      });

      // 基本的なイベントハンドラ
      wavesurfer.on("ready", () => {
        wavesurfer.play();
        setIsPlaying(true);
      });

      wavesurfer.on("finish", () => {
        setIsPlaying(false);
        setPlayingUrl(null);
        setCurrentSound(null);
      });

      // URLを読み込み
      wavesurfer.load(url);
      setWavesurferInstance(wavesurfer);
      return wavesurfer;
    },
    [wavesurferInstance]
  );

  // 音声のBlobデータを取得するメソッド
  const downloadSound = async (uuid: string): Promise<Blob> => {
    // APIからBlobデータを取得
    const response = await fetch(`/api/v1/juno/sound/download/${uuid}`);
    if (!response.ok) {
      throw new Error("Failed to download sound");
    }
    return response.blob();
  };

  // 音声の再生切り替え
  const toggleAudio = async (url: string) => {
    if (!url || isProcessing.current) return;
    isProcessing.current = true;

    try {
      if (url === playingUrl && wavesurferInstance) {
        // 同じ音声の再生/停止を切り替え
        if (isPlaying) {
          wavesurferInstance.pause();
          setIsPlaying(false);
        } else {
          wavesurferInstance.play();
          setIsPlaying(true);
        }
      } else {
        // 新しい音声を再生
        // 現在再生中の音声があれば停止
        if (wavesurferInstance && isPlaying) {
          wavesurferInstance.pause();
        }

        // 音声情報を更新
        setPlayingUrl(url);
        const soundObj = sounds.find((s) => s.url === url) || null;
        setCurrentSound(soundObj);

        if (!soundObj?.uuid) {
          console.error("Sound UUID not found");
          isProcessing.current = false;
          return;
        }

        // blobURLをキャッシュまたは取得
        let blobUrl = "";
        if (soundBlobs[soundObj.uuid]) {
          blobUrl = URL.createObjectURL(soundBlobs[soundObj.uuid]);
        } else {
          try {
            const blob = await downloadSound(soundObj.uuid);
            setSoundBlobs((prev) => ({ ...prev, [soundObj.uuid]: blob }));
            blobUrl = URL.createObjectURL(blob);
          } catch (error) {
            console.error("Error downloading sound:", error);
            isProcessing.current = false;
            return;
          }
        }

        // 波形を作成して再生
        if (waveformRef.current) {
          createWavesurfer(waveformRef.current, blobUrl);
        }
      }
    } catch (error) {
      console.error("Error toggling audio:", error);
    } finally {
      isProcessing.current = false;
    }
  };

  // 音声をダウンロード（外部からの利用のために提供）
  const downloadSoundFile = async (uuid: string, filename: string) => {
    try {
      const blob = await downloadSound(uuid);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading sound:", error);
    }
  };

  // 特定位置から再生
  const seekTo = useCallback(
    (position: number) => {
      if (!wavesurferInstance || isProcessing.current) return;

      try {
        isProcessing.current = true;
        wavesurferInstance.seekTo(position);
        isProcessing.current = false;
      } catch (e) {
        console.error("Error seeking:", e);
        isProcessing.current = false;
      }
    },
    [wavesurferInstance]
  );

  // ミュート切り替え
  const toggleMute = useCallback(() => {
    if (!wavesurferInstance) return;

    wavesurferInstance.setMuted(!isMuted);
    setIsMuted(!isMuted);
  }, [wavesurferInstance, isMuted]);

  // 前の音声へ
  const previousSound = useCallback(() => {
    if (!currentSound || sounds.length === 0 || !playingUrl || isProcessing.current) return;

    const currentIndex = sounds.findIndex((s) => s.url === playingUrl);
    if (currentIndex > 0) {
      const prevSound = sounds[currentIndex - 1];
      if (prevSound.url) {
        toggleAudio(prevSound.url);
      }
    }
  }, [playingUrl, currentSound, sounds, toggleAudio]);

  // 次の音声へ
  const nextSound = useCallback(() => {
    if (!currentSound || sounds.length === 0 || !playingUrl || isProcessing.current) return;

    const currentIndex = sounds.findIndex((s) => s.url === playingUrl);
    if (currentIndex < sounds.length - 1) {
      const nextSound = sounds[currentIndex + 1];
      if (nextSound.url) {
        toggleAudio(nextSound.url);
      }
    }
  }, [playingUrl, currentSound, sounds, toggleAudio]);

  // アンマウント時のクリーンアップ
  useEffect(() => {
    return () => {
      if (wavesurferInstance) {
        try {
          wavesurferInstance.destroy();
        } catch (e) {
          console.error("Error destroying wavesurfer on unmount:", e);
        }
      }
    };
  }, [wavesurferInstance]);

  return (
    <SoundContext.Provider
      value={{
        fetchSoundTrigger,
        setFetchSoundTrigger,
        prompt,
        setPrompt,
        lyric,
        setLyric,
        negativePrompt,
        setNegativePrompt,
        processing,
        setProcessing,
        aiEngine,
        setSoundEngine,
        category,
        setCategory,
        duration,
        setDuration,
        guidanceScale,
        setGuidanceScale,
        step,
        setStep,
        audio1,
        setAudio1,
        public: isPublic,
        setPublic,
        credit,
        isCalculatingCredit,
        submitSound,
        updateParams,

        // 波形と再生機能のためのプロパティ
        wavesurferInstance,
        waveformRef,
        isPlaying,
        isMuted,
        playingUrl,
        currentSound,
        sounds,
        setSounds,
        toggleAudio,
        toggleMute,
        previousSound,
        nextSound,
        downloadSoundFile,
        seekTo,
      }}
    >
      {children}
    </SoundContext.Provider>
  );
};
